import { AnimateHeight, Caption, Flex, propTypes, View } from '@tabeo/scarf'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { Link, matchPath, useLocation, useMatch } from 'react-router'

function NestedNav({ children, icon, title, to, alwaysOpen, ...rest }) {
  const subRoutes = React.Children.toArray(children)
    .flatMap(c => [c.props?.to, ...(c.props?.activePaths || [])])
    .filter(Boolean)
  const location = useLocation()

  // subRoutes is a dynamic-length array, so use matchPath (a plain function)
  // rather than a variable number of useMatch hooks. v6 matchPath takes
  // (pattern, pathname) and { end: false } preserves v5's prefix matching.
  const match = subRoutes.some(
    r => r && matchPath({ path: r, end: false }, location.pathname)
  )
  // v5's useRouteMatch(to)?.isExact maps to a truthy (exact) useMatch(to). The
  // hook must run unconditionally, so use a sentinel pattern when `to` is absent.
  const parentRouteMatch = !!useMatch(to || '/__no_match__')
  const parentMatch = to && !match && parentRouteMatch

  const [open, setOpen] = useState(match || parentMatch)

  const isActive = (!!subRoutes.length && !!match) || parentMatch

  useEffect(() => {
    setOpen(isActive || alwaysOpen)
  }, [isActive, location.pathname, alwaysOpen])

  return (
    <View>
      <Flex
        role={!alwaysOpen ? 'group' : undefined}
        justifyContent="flex-start"
        px={4}
        py={2}
        position="relative"
        _hover={
          !alwaysOpen
            ? {
                bg: 'sky.2',
              }
            : undefined
        }
        _active={
          !alwaysOpen
            ? {
                bg: 'sky.2',
              }
            : undefined
        }
        cursor={!alwaysOpen ? 'pointer' : undefined}
        onClick={() => {
          setOpen(!open || alwaysOpen)
        }}
        bg={parentMatch && 'sky.3'}
        as={to ? Link : undefined}
        to={to || undefined}
        {...rest}
      >
        {parentMatch && (
          <View
            position="absolute"
            left="0"
            width="3px"
            height="100%"
            bg="primary.3"
          />
        )}
        {icon && (
          <View
            as={icon}
            mr={2}
            width={['24px', '20px']}
            height={['24px', '20px']}
            color={isActive ? 'primary.3' : 'ink.2'}
            _groupHover={{
              color: isActive ? 'primary.3' : 'ink.0',
            }}
          />
        )}
        <Caption
          color={isActive ? 'primary.3' : 'ink.0'}
          fontWeight={isActive ? 'semibold' : 'regular'}
          fontSize={['16px', '14px']}
        >
          {title}
        </Caption>
      </Flex>
      <AnimateHeight duration={200} height={isActive || open ? 'auto' : 0}>
        {children}
      </AnimateHeight>
    </View>
  )
}
NestedNav.propTypes = {
  children: propTypes.children,
  title: PropTypes.string,
  icon: PropTypes.object,
  to: PropTypes.string,
  alwaysOpen: PropTypes.bool,
}

export default NestedNav
